/** Shown until an actor exists: Entra sign-in, or the seeded-user picker in AUTH_MODE=dev. */
import { useEffect, useState } from 'react';
import { LogIn, ShieldCheck, UserRound } from 'lucide-react';
import { apiFetch } from '@/api/client';
import { endpoints } from '@/api/endpoints';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from './AuthProvider';

/** AUTH_MODE=dev: POST the chosen seeded user, then remember it for this browser. */
async function signInAsDevUser(
  userId: string,
  remember: (id: string) => void,
  onError: (message: string | null) => void,
): Promise<void> {
  onError(null);
  try {
    await apiFetch(endpoints.devLogin, { method: 'POST', body: { userId } });
    remember(userId);
  } catch (err) {
    onError(err instanceof Error ? err.message : 'Dev sign-in failed');
  }
}

interface DevUser {
  id: string;
  displayName: string;
  roles?: string[];
  department?: string | null;
}

function normaliseDevUsers(raw: unknown): DevUser[] {
  const list = Array.isArray(raw) ? raw : ((raw as { users?: unknown[] })?.users ?? []);
  return (list as Record<string, unknown>[]).map((u) => ({
    id: String(u.id ?? u.userId ?? u.key ?? ''),
    displayName: String(u.displayName ?? u.display_name ?? u.name ?? u.email ?? 'Seeded user'),
    roles: normaliseRoles(u.roles),
    department: (u.department as string | undefined) ?? (u.scopeName as string | undefined) ?? null,
  }));
}

function normaliseRoles(raw: unknown): string[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  return raw.map((r) => (typeof r === 'string' ? r : String((r as { roleCode?: string }).roleCode ?? '')));
}

export function SignInScreen() {
  const { mode, signIn, setDevUser } = useAuth();
  const [users, setUsers] = useState<DevUser[] | null>(null);
  const [loading, setLoading] = useState(mode === 'dev');
  const [manual, setManual] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (mode !== 'dev') return;
    let cancelled = false;
    apiFetch<unknown>(endpoints.devUsers)
      .then((raw) => {
        if (!cancelled) setUsers(normaliseDevUsers(raw).filter((u) => u.id));
      })
      .catch(() => {
        if (!cancelled) setUsers([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mode]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="mb-2 flex items-center gap-2 text-primary">
            <ShieldCheck className="h-5 w-5" aria-hidden />
            <span className="id-mono text-xs uppercase tracking-widest">Beeline Medicines</span>
          </div>
          <CardTitle className="text-2xl">Capability Investment Governance</CardTitle>
          <CardDescription>
            {mode === 'entra'
              ? 'Sign in with your Beeline account to open the capability registry.'
              : 'Development sign-in. Pick the seeded user you want the API to act as — permissions are enforced server-side exactly as they are for Entra sign-in.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {mode === 'entra' ? (
            <Button className="w-full" onClick={signIn}>
              <LogIn className="mr-2 h-4 w-4" aria-hidden />
              Sign in with Microsoft
            </Button>
          ) : loading ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : users && users.length > 0 ? (
            <ul className="space-y-2">
              {users.map((u) => (
                <li key={u.id}>
                  <button
                    type="button"
                    onClick={() => void signInAsDevUser(u.id, setDevUser, setError)}
                    className="flex w-full items-center gap-3 rounded-md border border-border bg-card px-3 py-2 text-left transition-colors duration-150 hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <UserRound className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">{u.displayName}</span>
                      <span className="id-mono block truncate text-xs text-muted-foreground">
                        {[u.department, u.roles?.join(' · ')].filter(Boolean).join(' — ')}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <form
              className="space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                if (manual.trim()) void signInAsDevUser(manual.trim(), setDevUser, setError);
              }}
            >
              <div className="space-y-1">
                <Label htmlFor="dev-user">Seeded user</Label>
                <Input
                  id="dev-user"
                  value={manual}
                  onChange={(e) => setManual(e.target.value)}
                  placeholder="00000000-0000-0000-0000-000000000000"
                  autoComplete="off"
                />
                <p className="text-xs text-muted-foreground">
                  The API has not published its seeded-user list yet. Paste a seeded user id; the API signs you in as that
                  user and applies their roles.
                </p>
              </div>
              <Button type="submit" className="w-full" disabled={!manual.trim()}>
                Continue
              </Button>
            </form>
          )}
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
        </CardContent>
      </Card>
    </div>
  );
}
